import React, { useState, useEffect } from 'react';
import './MembershipPackagesPage.css';
import SellerDashboardLayout from '../components/SellerDashboardLayout';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const MembershipPackagesPage = () => {
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [currentPlan, setCurrentPlan] = useState('basic');
  const [plans, setPlans] = useState([]);
  const [plansData, setPlansData] = useState([]); // Full API data with features
  const [loading, setLoading] = useState(false);
  
  // Annual discount from admin settings
  const [annualDiscount, setAnnualDiscount] = useState({ value: 40, isVisible: true });

  // View state: 'plans' or 'badge-form'
  const [currentView, setCurrentView] = useState('plans');
  const [selectedBadge, setSelectedBadge] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null);

  // Document upload states
  const [documents, setDocuments] = useState({
    businessRegistration: null,
    identityProof: null,
    vatCertificate: null,
    businessLicense: null,
    proofOfAddress: null
  });

  // Payment form states
  const [paymentForm, setPaymentForm] = useState({
    cardName: '',
    cardNumber: '',
    cvv: '',
    expiryDate: '',
    country: 'Bangladesh'
  });

  const defaultPlans = [
    {
      id: 'basic',
      dbId: 1,
      name: 'Basic',
      subtitle: 'For those starting the platform',
      monthlyPrice: 0,
      annualPrice: 0,
      color: '#1f396c',
      features: {
        productPostingLimit: 10,
        canReceiveRFQs: true,
        buyerContactRegion: 'global',
        canViewBuyerContacts: false,
        canReceiveDirectCalls: false,
        buyerDirectoryAccess: false,
        canUploadVideos: false,
        maxProductImages: 5, // 5 images for Basic
        hasCompanyProfile: true,
        canLiveChat: false,
        canHighlightBrand: false,
        hasDashboardAccess: true,
        canAddProfessional: true,
        canSubmitProjects: false,
        canPostNews: false,
        trustedBadgeAvailable: false,
      },
    },
    {
      id: 'gcc',
      dbId: 2,
      name: 'GCC Business',
      subtitle: 'For GCC-based companies',
      monthlyPrice: 5,
      annualPrice: 49,
      color: '#f59e0b',
      popular: true,
      features: {
        productPostingLimit: 100,
        canReceiveRFQs: true,
        buyerContactRegion: 'gcc',
        canViewBuyerContacts: true,
        canReceiveDirectCalls: false, // GCC Business does NOT get direct calls
        buyerDirectoryAccess: true,
        buyerDirectoryRegion: 'gcc',
        canUploadVideos: true,
        maxProductImages: 5, // Same as Basic
        hasCompanyProfile: true,
        canLiveChat: true,
        canHighlightBrand: true,
        hasDashboardAccess: true,
        canAddProfessional: true,
        canSubmitProjects: true,
        canPostNews: true,
        trustedBadgeAvailable: 'Available with Extra Fee',
      },
    },
    {
      id: 'global',
      dbId: 3,
      name: 'Global Enterprise',
      subtitle: 'For Global companies',
      monthlyPrice: 10,
      annualPrice: 99,
      color: '#10b981',
      features: {
        productPostingLimit: 200,
        canReceiveRFQs: true,
        buyerContactRegion: 'global',
        canViewBuyerContacts: true,
        canReceiveDirectCalls: true, // ONLY Global Enterprise gets direct calls
        buyerDirectoryAccess: true,
        buyerDirectoryRegion: 'global',
        canUploadVideos: true,
        maxProductImages: 5, // Same as others
        hasCompanyProfile: true,
        canLiveChat: true,
        canHighlightBrand: true,
        hasDashboardAccess: true,
        canAddProfessional: true,
        canSubmitProjects: true,
        canPostNews: true,
        trustedBadgeAvailable: 'Available with Extra Fee',
      },
    },
  ];

  const countries = [
    'Bangladesh', 'India', 'Pakistan', 'UAE', 'Saudi Arabia', 'Qatar', 
    'Kuwait', 'Oman', 'Bahrain', 'United States', 'United Kingdom'
  ];

  // Feature definitions - maps API keys to display names
  const featureDefinitions = [
    { key: 'productPostingLimit', name: 'Product / Service Posting Limit' },
    { key: 'canReceiveRFQs', name: 'Received Buy Leads / RFQs', regionKey: 'rfqRegion' },
    { key: 'canViewBuyerContacts', name: 'View Buyer Contact Details', regionKey: 'buyerContactRegion' },
    { key: 'canReceiveDirectCalls', name: 'Received Direct Calls from Buyers' },
    { key: 'buyerDirectoryAccess', name: 'Buyer Directory Access', regionKey: 'buyerDirectoryRegion' },
    { key: 'canUploadVideos', name: 'Upload Videos' },
    { key: 'maxProductImages', name: 'Max Product Images per listing' },
    { key: 'hasCompanyProfile', name: 'Company Profile with Logo' },
    { key: 'canLiveChat', name: 'Live Chat with Buyers' },
    { key: 'canHighlightBrand', name: 'Highlight Your Brand (Optional)' },
    { key: 'hasDashboardAccess', name: 'Supplier Dashboard Access' },
    { key: 'canAddProfessional', name: 'Add Profile to Professionals Section' },
    { key: 'canSubmitProjects', name: 'Submit Projects in Projects Section' },
    { key: 'canPostNews', name: 'Post News in News Section' },
    { key: 'trustedBadgeAvailable', name: 'Trusted Supplier Badge (1 add on)' },
  ];

  // Generate dynamic feature rows from API data
  const getFeatureValue = (plan, featureDef) => {
    if (!plan?.features) return false;
    const value = plan.features[featureDef.key];
    
    // Handle numeric values (like productPostingLimit, maxProductImages)
    if (typeof value === 'number') return value.toString();
    
    // Handle region-based features - show region text when the base feature is true
    if (featureDef.regionKey && value === true) {
      const region = plan.features[featureDef.regionKey];
      // For RFQ region, show "Global" or "Only GCC"
      if (featureDef.regionKey === 'rfqRegion') {
        if (region === 'gcc') return 'Only GCC';
        if (region === 'global' || region === 'all') return 'Global';
      }
      // For buyer contact and directory access, show "(All Buyers)" or "(GCC Buyers)"
      if (featureDef.regionKey === 'buyerContactRegion' || featureDef.regionKey === 'buyerDirectoryRegion') {
        if (region === 'gcc') return '(GCC Buyers)';
        if (region === 'global' || region === 'all') return '(All Buyers)';
      }
      if (region) return region;
      return true; // Feature is enabled but no specific region
    }
    
    // Handle trusted badge special case (can be boolean or string)
    if (featureDef.key === 'trustedBadgeAvailable') {
      if (value === true) return 'Available with Extra Fee';
      if (typeof value === 'string' && value) return value;
      return false;
    }
    
    return value;
  };

  const badgeInfo = {
    verified: {
      title: 'Verified',
      price: 99,
      description: 'Once your company documents are reviewed and validated, a Verified Badge will be displayed on your profile.',
      requirements: [
        { id: 1, label: 'Business Registration Certificate', field: 'businessRegistration' },
        { id: 2, label: 'Title or VAT Certificate', field: 'vatCertificate' },
        { id: 3, label: 'Company Owner/Director ID Proof (Passport, National ID, or Driver\'s License)', field: 'identityProof' },
        { id: 4, label: 'Business License or Operating Permit', field: 'businessLicense' },
        { id: 5, label: 'Proof of Company Address (Utility Bill or lease agreement)', field: 'proofOfAddress' }
      ]
    },
    inspected: {
      title: 'Inspected',
      price: 249,
      description: 'Once your company On-site inspection with photo and Video documentation, a Inspected Badge will be displayed on your profile.',
      requirements: [
        { id: 1, label: 'Business Registration Certificate', field: 'businessRegistration' },
        { id: 2, label: 'Title or VAT Certificate', field: 'vatCertificate' },
        { id: 3, label: 'Company Owner/Director ID Proof (Passport, National ID, or Driver\'s License)', field: 'identityProof' },
        { id: 4, label: 'Business License or Operating Permit', field: 'businessLicense' },
        { id: 5, label: 'Proof of Company Address (Utility Bill or lease agreement)', field: 'proofOfAddress' }
      ]
    },
    certified: {
      title: 'Certified',
      price: 499,
      description: 'Once your company Documents + on-site visit + client review, a Certified Badge will be displayed on your profile.',
      requirements: [
        { id: 1, label: 'Business Registration Certificate', field: 'businessRegistration' },
        { id: 2, label: 'Title or VAT Certificate', field: 'vatCertificate' },
        { id: 3, label: 'Company Owner/Director ID Proof (Passport, National ID, or Driver\'s License)', field: 'identityProof' },
        { id: 4, label: 'Business License or Operating Permit', field: 'businessLicense' },
        { id: 5, label: 'Proof of Company Address (Utility Bill or lease agreement)', field: 'proofOfAddress' }
      ]
    }
  };

  const planColors = ['#1f396c', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Add cache-busting timestamp and request settings to be included
        const response = await fetch(`${API_URL}/membership/plans?includeSettings=true&t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        if (response.ok) {
          const responseData = await response.json();
          
          // Handle both new format (with settings) and old format (array)
          const plansArray = responseData.plans || responseData;
          const settings = responseData.settings || {};
          
          // Update annual discount from settings
          if (settings.annualDiscount) {
            setAnnualDiscount({
              value: settings.annualDiscount.value || 40,
              isVisible: settings.annualDiscount.isVisible !== false
            });
          }
          
          if (plansArray && plansArray.length > 0) {
            // Store full API data with features
            setPlansData(plansArray);
            
            const transformedPlans = plansArray.map((plan, index) => ({
              id: plan.name?.toLowerCase() || `plan-${index}`,
              dbId: plan.id,
              name: plan.displayName || plan.name,
              subtitle: plan.description || '',
              monthlyPrice: plan.monthlyPrice || 0,
              annualPrice: plan.annualPrice || 0,
              color: planColors[index % planColors.length],
              popular: index === 1,
              features: plan.features, // Include features
            }));
            setPlans(transformedPlans);
          } else {
            setPlans(defaultPlans);
            setPlansData([]);
          }
        } else {
          setPlans(defaultPlans);
          setPlansData([]);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPlans(defaultPlans);
        setPlansData([]);
      }
    };

    const fetchUserSubscription = async () => {
      if (!user?.id) return;
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // First try to get seller profile which has the actual membershipPlan
        const profileResponse = await fetch(`${API_URL}/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          
          // Get membershipPlan from seller profile
          if (profileData?.sellerProfile?.membershipPlan) {
            const plan = profileData.sellerProfile.membershipPlan.toLowerCase().replace(/\s+/g, '-');
            console.log('Current plan from seller profile:', plan);
            setCurrentPlan(plan);
            return;
          }
        }

        // Fallback to subscription API
        const response = await fetch(`${API_URL}/membership/subscription/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          if (data?.plan?.name) {
            setCurrentPlan(data.plan.name.toLowerCase().replace(/\s+/g, '-'));
          }
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    };

    fetchData();
    fetchUserSubscription();

    // Refetch data when window gains focus to get latest admin changes
    const handleFocus = () => {
      console.log('Window focused - refetching membership plans...');
      fetchData();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handlePaymentChange = (field, value) => {
    setPaymentForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      setDocuments(prev => ({ ...prev, [field]: file }));
    }
  };

  const handleBadgeClick = (badgeType) => {
    setSelectedBadge(badgeType);
    setCurrentView('badge-form');
    // Reset documents
    setDocuments({
      businessRegistration: null,
      identityProof: null,
      vatCertificate: null,
      businessLicense: null,
      proofOfAddress: null
    });
  };

  const handleCancel = () => {
    setCurrentView('plans');
    setSelectedBadge(null);
  };

  const handleDocumentSubmit = () => {
    setShowPaymentModal(true);
  };

  const handleUpgrade = (plan) => {
    // If it's the current plan, don't do anything
    if (isCurrentPlan(plan)) return;
    setSelectedPlanForUpgrade(plan);
    setShowUpgradeModal(true);
  };

  const handlePlanUpgradeSubmit = async () => {
    if (!selectedPlanForUpgrade || !user?.id) {
      alert('Please login to upgrade your plan');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const planId = selectedPlanForUpgrade.dbId || selectedPlanForUpgrade.id;
      
      const response = await fetch(`${API_URL}/membership/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.id,
          planId: planId,
          billingPeriod: billingCycle
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Update the current plan in state
        setCurrentPlan(normalizePlanName(selectedPlanForUpgrade.name || selectedPlanForUpgrade.id));
        alert(`Successfully upgraded to ${selectedPlanForUpgrade.name} plan!`);
        setShowUpgradeModal(false);
        setSelectedPlanForUpgrade(null);
        // Reload the page to refresh all data
        window.location.reload();
      } else {
        alert(data.message || 'Failed to upgrade plan. Please try again.');
      }
    } catch (error) {
      console.error('Error upgrading plan:', error);
      alert('Failed to upgrade plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBadgePaymentSubmit = async () => {
    if (!selectedBadge || !user?.id) {
      alert('Please login to purchase a badge');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const badgeId = selectedBadge === 'verified' ? 1 : selectedBadge === 'inspected' ? 2 : 3;
      
      const response = await fetch(`${API_URL}/membership/purchase-badge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.id,
          badgeId: badgeId
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        alert(`Successfully purchased ${badgeInfo[selectedBadge]?.title} badge!`);
        setShowPaymentModal(false);
        setCurrentView('plans');
        setSelectedBadge(null);
      } else {
        alert(data.message || 'Failed to purchase badge. Please try again.');
      }
    } catch (error) {
      console.error('Error purchasing badge:', error);
      alert('Failed to purchase badge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (type) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      alert(`${type} payment successful!`);
      setShowPaymentModal(false);
      setShowUpgradeModal(false);
      setCurrentView('plans');
      setSelectedBadge(null);
    } catch (error) {
      alert('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (plan) => {
    if (plan.monthlyPrice === 0 && plan.annualPrice === 0) return 'Free';
    const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    return `$${price}`;
  };

  const getPeriod = (plan) => {
    if (plan.monthlyPrice === 0 && plan.annualPrice === 0) return '';
    return billingCycle === 'annual' ? '/mo' : '/mo';
  };

  // Helper to normalize plan names for comparison
  const normalizePlanName = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/\s+/g, '-');
  };

  const isCurrentPlan = (plan) => {
    const planId = normalizePlanName(plan.id);
    const planName = normalizePlanName(plan.name);
    const current = normalizePlanName(currentPlan);
    
    // Match various formats: 'gcc' matches 'gcc-business', 'global' matches 'global-enterprise'
    return planId === current || 
           planName === current || 
           (planId === 'gcc' && current === 'gcc-business') ||
           (planId === 'global' && current === 'global-enterprise') ||
           (current === 'basic' && planId === 'basic');
  };

  const getButtonText = (plan) => {
    if (isCurrentPlan(plan)) return 'Current Plan';
    return 'Upgrade Plan';
  };

  const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect width="20" height="20" rx="4" fill="#10b981"/>
      <path d="M6 10l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CrossIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect width="20" height="20" rx="4" fill="#ef4444"/>
      <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const renderCell = (value) => {
    if (value === true) return <CheckIcon />;
    if (value === false) return <CrossIcon />;
    // For region-based features like "GCC Buyers" or "All Buyers", show checkmark with text
    if (value === 'GCC Buyers' || value === 'All Buyers') {
      return (
        <div className="mp-cell-with-check">
          <CheckIcon />
          <span className="mp-cell-region-text">{value}</span>
        </div>
      );
    }
    return <span className="mp-cell-text">{value}</span>;
  };

  // Payment Gateway Modal Component
  const PaymentGatewayModal = ({ show, onClose, title, description, onSubmit }) => {
    if (!show) return null;
    
    return (
      <div className="mp-modal-overlay" onClick={onClose}>
        <div className="mp-payment-modal" onClick={(e) => e.stopPropagation()}>
          <button className="mp-modal-close-btn" onClick={onClose}>×</button>
          
          {description && (
            <p className="mp-payment-info-text">{description}</p>
          )}
          
          <div className="mp-payment-gateway-section">
            <h3 className="mp-payment-title">Payment gateway</h3>
            <label className="mp-radio-option">
              <input type="radio" name={`gateway-${title}`} defaultChecked />
              <span className="mp-stripe-text">stripe</span>
            </label>
          </div>

          <div className="mp-payment-details">
            <h4 className="mp-payment-subtitle">Payment Details</h4>
            
            <input 
              type="text"
              placeholder="Enter Name on Card"
              className="mp-payment-input"
              value={paymentForm.cardName}
              onChange={(e) => handlePaymentChange('cardName', e.target.value)}
            />
            
            <input 
              type="text"
              placeholder="Card Number"
              className="mp-payment-input"
              value={paymentForm.cardNumber}
              onChange={(e) => handlePaymentChange('cardNumber', e.target.value)}
            />
            
            <div className="mp-payment-row">
              <input 
                type="text"
                placeholder="CVV Code"
                className="mp-payment-input-half"
                value={paymentForm.cvv}
                onChange={(e) => handlePaymentChange('cvv', e.target.value)}
              />
              <input 
                type="text"
                placeholder="Expiration Date"
                className="mp-payment-input-half"
                value={paymentForm.expiryDate}
                onChange={(e) => handlePaymentChange('expiryDate', e.target.value)}
              />
            </div>

            <p className="mp-payment-terms">
              By Clicking "Confirm Payment" I agree to the companies term of services
            </p>

            <div className="mp-country-section">
              <label className="mp-country-label">Country</label>
              <select 
                className="mp-country-select"
                value={paymentForm.country}
                onChange={(e) => handlePaymentChange('country', e.target.value)}
              >
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>

          <button 
            className="mp-confirm-btn"
            onClick={onSubmit}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>
    );
  };

  // Badge Document Upload View
  const BadgeFormView = () => {
    const badge = badgeInfo[selectedBadge];
    if (!badge) return null;

    return (
      <div className="mp-badge-form-container">
        <div className="mp-badge-form-card">
          <div className="mp-badge-form-header">
            <h2 className="mp-section-title">Membership Packages</h2>
            <p className="mp-section-subtitle">
              Flexible plans designed for suppliers, buyers, and professionals at every level.
            </p>
          </div>

          <div className="mp-verification-section">
            <h3 className="mp-verification-title">Verification Badges (Optional Add-ons)</h3>
            <p className="mp-verification-subtitle">
              Boost your credibility and stand out — choose the badge level that matches your trust goals.
            </p>

            {/* Selected Badge Info */}
            <div className="mp-selected-badge">
              <span className="mp-badge-label">Verify</span>
              <span className="mp-badge-price">${badge.price}/year</span>
              <span className="mp-badge-note">Upload header Company documents for reviewing</span>
            </div>

            {/* Document Upload Section */}
            <div className="mp-documents-section">
              {badge.requirements.map((req, index) => (
                <div key={req.id} className="mp-document-item">
                  <div className="mp-document-header">
                    <span className="mp-document-number">{index + 1}.</span>
                    <span className="mp-document-label">{req.label}</span>
                  </div>
                  <div className="mp-document-upload">
                    <label className={`mp-choose-file-btn ${documents[req.field] ? 'uploaded' : ''}`}>
                      <input 
                        type="file" 
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(req.field, e)}
                        hidden
                      />
                      {documents[req.field] ? '✓ Choose File' : 'Choose File'}
                    </label>
                    <span className="mp-upload-status">
                      {documents[req.field] ? 'Upload is submitted' : 'Upload any document'}
                    </span>
                  </div>
                  <p className="mp-file-hint">
                    Allowed file type: pdf/pdf files(s) (Size up to 10 MB)
                  </p>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="mp-action-buttons">
              <button className="mp-cancel-btn" onClick={handleCancel}>Cancel</button>
              <button className="mp-submit-btn" onClick={handleDocumentSubmit}>Submit</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Plans Comparison View
  const PlansView = () => (
    <>
      {/* Header */}
      <div className="mp-header">
        <div className="mp-header-left">
          <h1 className="mp-title">Membership Packages</h1>
          <p className="mp-subtitle">Flexible plans designed for suppliers, buyers, and professionals at every level.</p>
        </div>
      </div>

      {/* Billing Toggle - positioned above GCC card */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', alignItems: 'center' }}>
        <div style={{ width: '437px', minWidth: '437px', flexShrink: 0 }}></div>
        <div style={{ flex: 1 }}></div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="mp-billing-toggle">
            <span className="mp-billing-option">Monthly</span>
            <label className="mp-toggle-switch">
              <input 
                type="checkbox" 
                checked={billingCycle === 'annual'}
                onChange={() => setBillingCycle(billingCycle === 'annual' ? 'monthly' : 'annual')}
              />
              <span className="mp-toggle-slider"></span>
            </label>
            <span className="mp-billing-option">Annual</span>
            <span className="mp-discount-text">{annualDiscount.value}% off</span>
          </div>
        </div>
        <div style={{ flex: 1 }}></div>
      </div>

      {/* Plans Cards Row - Dynamically rendered from API */}
      <div className="mp-plans-row">
        {/* Empty label cell for alignment with features table */}
        <div className="mp-label-cell"></div>
        
        {/* Dynamic Plan Cards */}
        {(plansData.length > 0 ? plansData : defaultPlans).map((plan, index) => {
          const planName = plan.name?.toLowerCase() || plan.displayName?.toLowerCase() || '';
          const isBasic = planName.includes('basic') || planName.includes('free');
          const isGCC = planName.includes('gcc') || planName.includes('business');
          const isGlobal = planName.includes('global') || planName.includes('enterprise');
          
          const cardClass = isBasic ? 'basic' : isGCC ? 'gcc' : isGlobal ? 'global' : 'basic';
          const displayName = plan.displayName || plan.name || 'Plan';
          const description = plan.description || '';
          const monthlyPrice = plan.monthlyPrice || 0;
          const annualPrice = plan.annualPrice || 0;
          
          const isCurrentPlanCheck = normalizePlanName(currentPlan) === planName || 
                                     normalizePlanName(currentPlan) === planName.replace(/\s+/g, '-');
          
          return (
            <div key={plan.id || index} className={`mp-plan-card ${cardClass}`}>
              <div className="mp-plan-content">
                <div className="mp-plan-info">
                  <h3 className="mp-plan-name">{displayName}</h3>
                  <p className="mp-plan-description">{description}</p>
                  <div className="mp-plan-pricing">
                    {isBasic && monthlyPrice === 0 && annualPrice === 0 ? (
                      <span className="mp-price-main">Free</span>
                    ) : (
                      <>
                        <div className="mp-price-section">
                          <div className="mp-price-main-row">
                            <span className="mp-price-amount">
                              ${billingCycle === 'annual' ? annualPrice : monthlyPrice}/
                            </span>
                            <span className="mp-price-period">
                              {billingCycle === 'annual' ? 'year' : 'month'}
                            </span>
                          </div>
                          {billingCycle === 'annual' && monthlyPrice > 0 && (
                            <span className="mp-price-original">${monthlyPrice * 12}</span>
                          )}
                        </div>
                        {billingCycle === 'annual' && monthlyPrice > 0 && annualPrice < monthlyPrice * 12 && (
                          <span className="mp-save-badge">Save ${(monthlyPrice * 12) - annualPrice}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <button 
                  className={`mp-plan-btn ${isCurrentPlanCheck ? 'current' : ''}`}
                  onClick={() => !isCurrentPlanCheck && handleUpgrade(plan)}
                  disabled={isCurrentPlanCheck}
                >
                  {isCurrentPlanCheck ? 'Current Plan' : 'Upgrade Plan'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table - Dynamically rendered from API data */}
      <div className="mp-features-comparison">
        {/* Header Row with Plan Names */}
        <div className="mp-table-header-row">
          <div className="mp-table-header-empty"></div>
          <div className="mp-table-header-values">
            {(plansData.length > 0 ? plansData : defaultPlans).map((plan, idx) => (
              <div key={idx} className="mp-table-header-cell">{plan.displayName || plan.name}</div>
            ))}
          </div>
        </div>

        {/* Dynamic Feature Rows from API */}
        {featureDefinitions.map((featureDef, rowIdx) => {
          const activePlans = plansData.length > 0 ? plansData : defaultPlans;
          return (
            <div key={rowIdx} className="mp-feature-row">
              <div className="mp-feature-name-cell">
                <span>{featureDef.name}</span>
              </div>
              <div className="mp-feature-values">
                {activePlans.map((plan, planIdx) => {
                  const value = getFeatureValue(plan, featureDef);
                  return (
                    <div key={planIdx} className="mp-feature-value-cell">
                      {value === true ? (
                        <CheckIcon />
                      ) : value === false ? (
                        <CrossIcon />
                      ) : typeof value === 'string' ? (
                        featureDef.regionKey || featureDef.key === 'canReceiveRFQs' ? (
                          <>
                            <CheckIcon />
                            <span>{value === 'global' ? 'Global' : value === 'gcc' ? 'Only GCC' : value}</span>
                          </>
                        ) : (
                          <span>{value}</span>
                        )
                      ) : (
                        <span>{value}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Message */}
      <div className="mp-footer-message">
        <p>Join thousands of businesses already using GulfConstructor to connect, grow, and succeed. Your membership isn't just a plan — it's a partnership for smarter trade.</p>
      </div>

      {/* Verification Badges Section */}
      <div className="mp-verification-badges-section">
        <div className="mp-verification-header">
          <h2 className="mp-verification-main-title">Verification Badges (Optional Add-ons)</h2>
          <p className="mp-verification-main-subtitle">Boost your credibility and stand out — choose the badge level that matches your trust goals.</p>
        </div>

        <div className="mp-badge-cards">
          {/* Verified Badge */}
          <div className="mp-badge-card verified-active">
            <div className="mp-badge-icon verified">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M31.7616 14.4591C31.2314 13.905 30.683 13.3341 30.4762 12.832C30.285 12.3722 30.2738 11.61 30.2625 10.8717C30.2414 9.49922 30.2189 7.94391 29.1375 6.8625C28.0561 5.78109 26.5008 5.75859 25.1283 5.7375C24.39 5.72625 23.6278 5.715 23.168 5.52375C22.6673 5.31703 22.095 4.76859 21.5409 4.23844C20.5706 3.30609 19.4681 2.25 18 2.25C16.5319 2.25 15.4308 3.30609 14.4591 4.23844C13.905 4.76859 13.3341 5.31703 12.832 5.52375C12.375 5.715 11.61 5.72625 10.8717 5.7375C9.49922 5.75859 7.94391 5.78109 6.8625 6.8625C5.78109 7.94391 5.76562 9.49922 5.7375 10.8717C5.72625 11.61 5.715 12.3722 5.52375 12.832C5.31703 13.3327 4.76859 13.905 4.23844 14.4591C3.30609 15.4294 2.25 16.5319 2.25 18C2.25 19.4681 3.30609 20.5692 4.23844 21.5409C4.76859 22.095 5.31703 22.6659 5.52375 23.168C5.715 23.6278 5.72625 24.39 5.7375 25.1283C5.75859 26.5008 5.78109 28.0561 6.8625 29.1375C7.94391 30.2189 9.49922 30.2414 10.8717 30.2625C11.61 30.2738 12.3722 30.285 12.832 30.4762C13.3327 30.683 13.905 31.2314 14.4591 31.7616C15.4294 32.6939 16.5319 33.75 18 33.75C19.4681 33.75 20.5692 32.6939 21.5409 31.7616C22.095 31.2314 22.6659 30.683 23.168 30.4762C23.6278 30.285 24.39 30.2738 25.1283 30.2625C26.5008 30.2414 28.0561 30.2189 29.1375 29.1375C30.2189 28.0561 30.2414 26.5008 30.2625 25.1283C30.2738 24.39 30.285 23.6278 30.4762 23.168C30.683 22.6673 31.2314 22.095 31.7616 21.5409C32.6939 20.5706 33.75 19.4681 33.75 18C33.75 16.5319 32.6939 15.4308 31.7616 14.4591Z" stroke="#547C92" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M24 13.5L15.75 21.75L12 18" stroke="#547C92" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="mp-badge-info-content">
              <h3 className="mp-badge-title">Verified</h3>
              <p className="mp-badge-price-text">$99/year</p>
              <p className="mp-badge-desc">Company documents reviewed and validated.</p>
            </div>
            <button className="mp-badge-action-btn active" onClick={() => handleBadgeClick('verified')}>
              Active
            </button>
          </div>

          {/* Inspected Badge */}
          <div className="mp-badge-card">
            <div className="mp-badge-icon">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.7483 0C21.305 0 27.4529 6.14798 27.4529 13.7046C27.4529 16.3405 26.7042 18.8042 25.4095 20.896L36 31.5277L31.5278 36L20.9366 25.3677C18.8454 26.6614 16.3828 27.4092 13.7483 27.4092C6.19172 27.4092 0.0437355 21.2616 0.0437355 13.7046C0.043663 6.14798 6.19137 0 13.7483 0ZM13.7483 25.301C20.1424 25.301 25.3446 20.099 25.3446 13.7046C25.3446 7.3106 20.1424 2.10825 13.7483 2.10825C7.35398 2.10825 2.15192 7.3106 2.15192 13.7046C2.15192 20.099 7.35398 25.301 13.7483 25.301Z" fill="#547C92"/>
                <path d="M13.7485 4.2168C18.9802 4.2168 23.2363 8.47317 23.2363 13.7046C23.2363 18.9362 18.9802 23.1924 13.7485 23.1924C8.51676 23.1924 4.26074 18.9362 4.26074 13.7046C4.26074 8.47317 8.51683 4.2168 13.7485 4.2168ZM13.7485 21.0841C17.8176 21.0841 21.128 17.7736 21.128 13.7046C21.128 9.6355 17.8176 6.32505 13.7485 6.32505C9.67945 6.32505 6.36899 9.6355 6.36899 13.7046C6.36899 17.7736 9.67945 21.0841 13.7485 21.0841Z" fill="#547C92"/>
                <path d="M12.6942 15.3772L10.2769 12.9598L8.78613 14.4507L12.6942 18.3588L18.7106 12.3424L17.2198 10.8516L12.6942 15.3772Z" fill="#547C92"/>
              </svg>
            </div>
            <div className="mp-badge-info-content">
              <h3 className="mp-badge-title">Inspected</h3>
              <p className="mp-badge-price-text">$249/year</p>
              <p className="mp-badge-desc">On-site inspection with photo and Video documentation</p>
            </div>
            <button className="mp-badge-action-btn upgrade" onClick={() => handleBadgeClick('inspected')}>
              Upgrade to Inspected
            </button>
          </div>

          {/* Certified Badge */}
          <div className="mp-badge-card">
            <div className="mp-badge-icon">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M43.3835 27.2828V27.3386C43.5216 31.682 43.6412 35.4304 41.006 38.8919C38.137 42.6642 34.1656 45.6767 29.8064 47.3955C26.5549 46.115 23.5132 44.1253 21.0029 41.6255C19.0663 39.6968 17.0129 37.3777 16.471 34.7796C16.1203 33.0874 16.1734 31.1242 16.2239 29.2274C16.2398 28.6669 16.2558 28.0293 16.2611 27.4236C16.2611 27.4156 16.2611 27.4077 16.2611 27.3997V27.3864C16.2664 26.5496 16.277 25.7181 16.2903 24.9132C16.3302 22.509 16.4019 20.0198 16.5108 17.5068L29.809 12.6028L37.866 15.5755L40.7669 12.4195L29.809 8.37891L12.4464 14.7838L12.3853 16.0616C12.202 19.9455 12.0984 23.7629 12.0745 27.405C12.0719 27.9762 12.0559 28.5633 12.04 29.1291C11.9815 31.2384 11.9231 33.4221 12.3641 35.5367C13.1105 39.1203 15.6103 41.9921 17.9612 44.3298C21.1038 47.4566 24.9424 49.8846 29.0652 51.3537L29.809 51.6194L30.5528 51.3537C33.3182 50.3681 35.9321 48.9681 38.3229 47.1962C40.6341 45.4828 42.6822 43.4585 44.4115 41.1845C45.9788 39.123 46.9431 36.8198 47.3602 34.1473C47.7162 31.8547 47.6445 29.6073 47.5701 27.2244V27.2138C47.5488 26.5549 47.5276 25.8722 47.517 25.1975C47.5037 24.4297 47.4904 23.6567 47.4718 22.9022" fill="#547C92" transform="scale(0.6) translate(-5, -5)"/>
                <path d="M17 13L23 8L30 15L17 28L8 19L13 14L17 18L17 13Z" stroke="#547C92" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" transform="translate(2, 4)"/>
              </svg>
            </div>
            <div className="mp-badge-info-content">
              <h3 className="mp-badge-title">Certified</h3>
              <p className="mp-badge-price-text">$499/year</p>
              <p className="mp-badge-desc">Documents + on-site visit + client review. Full trust package.</p>
            </div>
            <button className="mp-badge-action-btn upgrade" onClick={() => handleBadgeClick('certified')}>
              Upgrade to Certified
            </button>
          </div>
        </div>

        <p className="mp-badges-footer-text">These badges help increase buyer trust and visibility on the platform.</p>
      </div>
    </>
  );

  return (
    <SellerDashboardLayout>
      <div className="mp-main-content">
        <div className="mp-container">
          {currentView === 'plans' ? <PlansView /> : <BadgeFormView />}
        </div>
      </div>

      {/* Payment Modal for Badge */}
      <PaymentGatewayModal 
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={badgeInfo[selectedBadge]?.title || ''}
        description={badgeInfo[selectedBadge]?.description || ''}
        onSubmit={handleBadgePaymentSubmit}
      />

      {/* Payment Modal for Plan Upgrade */}
      <PaymentGatewayModal 
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title={selectedPlanForUpgrade?.name || 'Upgrade'}
        description={`Upgrade to ${selectedPlanForUpgrade?.name} plan for ${formatPrice(selectedPlanForUpgrade || {})}${getPeriod(selectedPlanForUpgrade || {})}`}
        onSubmit={handlePlanUpgradeSubmit}
      />
    </SellerDashboardLayout>
  );
};

export default MembershipPackagesPage;
